import { LoadingProductTileSkeleton } from "@faire/deprecated-retailer-visitor-shared/components/LoadingProductTileSkeleton/LoadingProductTileSkeleton";
import { getAltTag } from "@faire/deprecated-retailer-visitor-shared/features/Marketplace/utils";
import { ILeanProductTileWithBrand } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/__internal__/adapters/LeanProtos/ILeanProductTile";
import { ProductTile } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/index";
import { IProductLinkProps } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { ProductTileType } from "@faire/deprecated-retailer-visitor-shared/features/MarketplaceItems/ProductTile/types";
import { useProductTileClickImpressions } from "@faire/deprecated-retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressions";
import { generateProductLinkPropsForLoggedOutRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/generateProductLinkPropsForLoggedOutRetailer";
import { isAdmin } from "@faire/deprecated-retailer-visitor-shared/lib/isAdmin";
import { useIsLoggedInRetailer } from "@faire/deprecated-retailer-visitor-shared/lib/isLoggedInRetailer";
import { brandPage } from "@faire/deprecated-retailer-visitor-shared/paths/index";
import { ProductPagePaths } from "@faire/deprecated-retailer-visitor-shared/paths/ProductPage";
import { getUser } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getUser";
import { CATEGORY_PRODUCT_TRACK_CLICK_CONTAINER_NAME } from "@faire/deprecated-retailer-visitor-shared/services/consts";
import { MarketplaceProductList } from "@faire/deprecated-retailer-visitor-shared/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/MainContent/__internal__/ProductOrBrandGrid/__internal__/ProductFirst/__internal__/MarketplaceProductList/MarketplaceProductList";
import { Surface } from "@faire/web-api--source/faire/logging/data/Surface";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { ProductTileContainer } from "@faire/web-api--source/indigofair/data/ProductTileContainer";
import { ProductTileScreenContainer } from "@faire/web-api--source/indigofair/data/ProductTileScreenContainer";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useEffectEvent } from "@faire/web/ui/hooks/useEffectEvent";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

export const Tile: React.FC<
  {
    tileData: ILeanProductTileWithBrand | undefined;
    product?: ILeanProductTile.ILeanProduct;
  } & Pick<
    MarketplaceProductList.IProps,
    | "title"
    | "pageType"
    | "filterKeys"
    | "category"
    | "showVerticalImagery"
    | "requestId"
  >
> = ({
  product,
  tileData,
  title,
  pageType,
  filterKeys,
  category,
  showVerticalImagery,
  requestId,
}) => {
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const {
    recordProductTileRightClickEvent,
    recordProductTileMiddleClickEvent,
  } = useProductTileClickImpressions();

  const { strictLocalize } = useStrictLocalization();
  const user = getUser();
  const location = useLocation();
  const getProductLinkPropsFromProductTile = (
    productTile?: ILeanProductTile
  ): IProductLinkProps | undefined => {
    const product = productTile?.product;
    if (!product?.token || !productTile) {
      return undefined;
    }

    const productPageLink = ProductPagePaths.index(product.token, {
      refF: filterKeys,
      refC: category?.navigationPath,
      productOptionToken: productTile.selected_product_option_token,
      impressionId: product.impression_id,
    });

    if (!isLoggedInRetailer && !isAdmin(user) && !isPrerender()) {
      return generateProductLinkPropsForLoggedOutRetailer({
        productToken: product.token,
        brandToken: product.brand_token,
        refF: filterKeys,
        refC: category?.navigationPath,
        productOptionToken: productTile.selected_product_option_token,
        location,
      });
    }

    return {
      productUrl: productPageLink,
    };
  };

  const getBrandUrlFromProductTile = (brand?: Pick<IBrand, "token">) => {
    if (!brand) {
      return undefined;
    }

    return brandPage(brand.token!, {
      page: 1,
      refC: category?.navigationPath,
      refF: filterKeys,
      refP: product?.token ? [product.token] : undefined,
      sortingHint: true,
      impressionId: product?.impression_id,
    });
  };

  const handleOnAuxClick = useEffectEvent(
    (event: React.MouseEvent<HTMLElement>) => {
      if (event.button === 1) {
        recordProductTileMiddleClickEvent();
      } else if (event.button === 2) {
        recordProductTileRightClickEvent();
      }
    }
  );

  if (tileData === undefined) {
    return <LoadingProductTileSkeleton />;
  }

  return (
    <TileWrapper
      $enableSearchResultCleanup={isLoggedInRetailer}
      onAuxClick={handleOnAuxClick}
    >
      <ProductTile
        product={tileData}
        brand={tileData?.brand}
        isLoggedIn={isLoggedInRetailer}
        type={ProductTileType.TILE_WITH_BRAND}
        tileInteractions={{
          props: {
            productLinkProps: getProductLinkPropsFromProductTile(tileData),
            brandLinkProps: {
              brandUrl: getBrandUrlFromProductTile(tileData?.brand) ?? "",
              brandToken: tileData?.brand?.token ?? "",
            },
          },
        }}
        imageConfigProps={{
          showVerticalImagery,
          altTag: getAltTag(
            {
              title,
              pageType,
              productName: tileData?.product?.name,
            },
            strictLocalize
          ),
        }}
        loggingContext={{
          impressionProviderProps: {
            context: `${ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID}|${
              category?.mostSpecificPathIdentifier ?? ""
            }`,
            productTileContainer:
              ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID,
            requestId: requestId ?? "",
            containerName: CATEGORY_PRODUCT_TRACK_CLICK_CONTAINER_NAME,
            isNewProductsCategory: category?.isNewProducts,
            screenContext: Surface.CATEGORY_PRODUCT_RESULTS,
            containerContext:
              ProductTileScreenContainer.CATEGORY_PRODUCT_RESULTS_PRODUCTS,
            contextToken: category?.mostSpecificPathIdentifier ?? "",
            trackedLinkContainerName: `${CATEGORY_PRODUCT_TRACK_CLICK_CONTAINER_NAME}_old`,
          },
        }}
      />
    </TileWrapper>
  );
};

const TileWrapper = styled(Column)<{
  $enableSearchResultCleanup: boolean;
}>`
  min-width: 0;
  width: 100%;
  padding: ${({ $enableSearchResultCleanup }) =>
    $enableSearchResultCleanup ? 0 : getSpacing("1x", 4, 0)};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${({ $enableSearchResultCleanup }) =>
      $enableSearchResultCleanup ? 0 : getSpacing("1x", "1x", 0)};
  }
`;
